import { useIngressesAndUploads } from "./lib/useIngressesAndUploads";
import { useUploader } from "lib/hooks/useUploader";
import { useUploadOperations } from "lib/hooks/useUploadOperations";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useAssets } from "lib/hooks/assets/useAssets";
import { getAssetPageType } from "lib/actions/assets/getAssetPageType";
import { dismissIngress } from "lib/hooks/useDismissIngress";
import { useRouter } from "next/router";
import IngressListItem from "./IngressListItem";
import { IngressListItemModel } from "./lib/IngressListItemModel";
import { FilterOption } from "./IngressJobsModal";
import { UploadStatus } from "lib/types/UploadStatus";

function formatDateHeader(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();

  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return "Today";
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
}

function getDateKey(dateString: string): string {
  const date = new Date(dateString);
  return date.toDateString(); // This gives us a consistent key for the same date
}

function groupIngressesByDate(
  ingresses: IngressListItemModel[],
): Record<string, IngressListItemModel[]> {
  return ingresses.reduce(
    (groups, ingress) => {
      const dateKey = getDateKey(ingress.createdOn);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(ingress);
      return groups;
    },
    {} as Record<string, IngressListItemModel[]>,
  );
}

function filterIngressesByStatus(
  ingresses: IngressListItemModel[],
  filter: FilterOption,
): IngressListItemModel[] {
  if (filter === "all") {
    return ingresses;
  }

  return ingresses.filter((ingress) => {
    const isInProgress =
      ingress.uploadStatus === UploadStatus.uploading ||
      ingress.uploadStatus === UploadStatus.preparing ||
      ingress.uploadStatus === UploadStatus.queued ||
      ingress.ingressStatus === "uploading" ||
      ingress.ingressStatus === "processing" ||
      ingress.ingressStatus === "awaitingProcessing";

    const isComplete =
      (ingress.ingressStatus === "done" ||
        ingress.ingressStatus === "error" ||
        ingress.ingressStatus === "partialError" ||
        ingress.uploadStatus === UploadStatus.error) &&
      ingress.ingressStatus !== "awaitingProcessing" &&
      ingress.uploadStatus !== UploadStatus.preparing;

    if (filter === "in-progress") {
      return isInProgress;
    }

    if (filter === "complete") {
      return isComplete;
    }

    return false;
  });
}

export default function IngressListItems({
  filter = "all",
  selectedExpandedItemId = null,
  onShowError,
  onClose,
}: {
  readonly filter?: FilterOption;
  readonly selectedExpandedItemId?: string | null;
  readonly onShowError?: (item: IngressListItemModel) => void;
  readonly onClose: () => void;
}) {
  const projectId = useCurrentProjectId();
  const { getAsset } = useAssets(projectId);
  const router = useRouter();
  const { cancelFile } = useUploader();
  const { pauseUpload, resumeUpload, cancelUpload } = useUploadOperations();
  const { ingressesAndUploads } = useIngressesAndUploads();

  const handleDelete = (jobId: string) => {
    cancelFile(jobId);
  };

  const handleDismiss = (jobId: string) => {
    const ingressId = parseInt(jobId);
    if (projectId && !isNaN(ingressId)) {
      dismissIngress(projectId, ingressId, () => cancelFile(jobId));
    }
  };

  // An import only knows its asset's id, so look the asset up to open its
  // model type's tab. A model that hasn't reached the asset list yet opens
  // under `other`, which shows any model without claiming a tab.
  const getModelAssetPageType = (assetId: string) => {
    const asset = getAsset(Number(assetId));
    return asset ? getAssetPageType(asset.type, asset.extension) : "other";
  };

  const handleViewModel = (item: IngressListItemModel) => {
    if (!item.assetId || !projectId) return;

    onClose();
    router.push({
      pathname: "/projects/[projectId]/[assetPageType]/[assetId]",
      query: {
        projectId: projectId,
        assetPageType: getModelAssetPageType(item.assetId),
        assetId: item.assetId,
      },
    });
  };

  const handlePause = (jobId: string) => {
    const item = ingressesAndUploads?.find((item) => item.id === jobId);
    if (item && item.ingressSource === "browserUpload") {
      pauseUpload(item);
    }
  };

  const handleResume = (jobId: string) => {
    const item = ingressesAndUploads?.find((item) => item.id === jobId);
    if (item && item.ingressSource === "browserUpload") {
      resumeUpload(item);
    }
  };

  const handleCancel = (jobId: string) => {
    const item = ingressesAndUploads?.find((item) => item.id === jobId);
    if (item && item.ingressSource === "browserUpload") {
      cancelUpload(item);
    }
  };

  const handleWhyDidThisHappen = (item: IngressListItemModel) => {
    if (onShowError) {
      onShowError(item);
    }
  };

  // Filter ingresses based on selected filter
  const filteredIngresses = ingressesAndUploads
    ? filterIngressesByStatus(ingressesAndUploads, filter)
    : [];

  // Group filtered ingresses by date and sort by most recent first
  const groupedIngresses =
    filteredIngresses.length > 0 ? groupIngressesByDate(filteredIngresses) : {};
  const sortedDateKeys = Object.keys(groupedIngresses).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        {filteredIngresses.length > 0 && (
          <span className="text-sm text-neutral-500">
            {filteredIngresses.length}{" "}
            {filteredIngresses.length === 1 ? "item" : "items"}
          </span>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto pr-4">
        {sortedDateKeys.map((dateKey) => {
          const ingressesForDate = groupedIngresses[dateKey];
          const displayDate = formatDateHeader(ingressesForDate[0].createdOn);

          return (
            <div key={dateKey} className="space-y-2 ">
              <div className="sticky top-0 bg-neutral-50 px-1 py-1">
                <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-600 ">
                  {displayDate}
                </h3>
              </div>
              <div className="space-y-2">
                {ingressesForDate.map((item) => (
                  <IngressListItem
                    key={item.id}
                    item={item}
                    title={item.name}
                    isExpanded={selectedExpandedItemId === item.id}
                    hasAnyExpanded={selectedExpandedItemId !== null}
                    onDelete={handleDelete}
                    onDismiss={handleDismiss}
                    onPause={handlePause}
                    onResume={handleResume}
                    onCancel={handleCancel}
                    onWhyDidThisHappen={handleWhyDidThisHappen}
                    onViewModel={handleViewModel}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {(!filteredIngresses || filteredIngresses.length === 0) && (
        <div className="items-top flex flex-1 justify-center text-neutral-500">
          <div className="text-center">
            <p>
              {filter === "all" && "No ingress jobs found"}
              {filter === "in-progress" && "No imports in progress"}
              {filter === "complete" && "No completed imports"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
